import React, { useMemo, useState, useRef, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  Segmented,
  SegmentedItem,
} from "@/components/common/segmented";
import { agentUserMessagesHooks } from "@/manager/manager/agent-user-messages-manager";
import { agentsHooks, assetsMaterialHooks, assetsTagsHook } from "@/manager";
import AgentVideoModal from "@/components/agent-modal-video";
import AgentMomentModal from "@/components/agent-modal-monent";
import AgentPostModal from "@/components/agent-modal-post";
import AgentAtricleModal from "@/components/agent-modal-atricle";
import {
  ShortVideoRecordItem,
  ShortVideoGeneratingItem,
  ShortVideoFailedItem,
  PodcastRecordItem,
  PodcastGeneratingItem,
  PodcastFailedItem,
  MomentsRecordItem,
  MomentsGeneratingItem,
  MomentsFailedItem,
  PostRecordItem,
  PostGeneratingItem,
  PostFailedItem,
  ArticleRecordItem,
  ArticleGeneratingItem,
  ArticleFailedItem,
  AiRecordItem,
  AiRecordList,
} from "@/components/agent-panel-records/record";
import dayjs from "dayjs";
import { ClockRewindIcon } from "../agent-creation/assets/clock-rewind-icon";
import { SubscribeInfoData } from "@/services/agent-v2-users-detail";
import { RecordEmptyImage } from "../agent-creation/assets/record-empty-image";
import { useTranslation } from "react-i18next";
import { LoadingOverlay } from "../common/loading-overlay";
import { useToast } from "@/hooks/use-toast";
import { TFunction } from "i18next";
import { cn } from "@/lib/utils";
import { Image } from "../common/image";
import { AssetsToolBar } from "../common/assets-tool-bar";
import { MaterialIcon } from "../assets/assets";
import { Tooltip } from "../ui/modal";
import { IconButton } from "../common/icon-button";
import { ModalTagMove } from "../assets-material-item/modal-tag-move";
import { PreviewVideoWithImageFull } from "../common/preview-video-with-image-full";
import { MessageItemData } from "@/services/push-info-origin-data-record-list";

type RecordType = 1 | 2 | 3 | 4 | 5 | 6;

export interface AgentPanelRecordsProps {
  isExpired?: boolean;
  expiredAt?: string;
  subscribeInfo: SubscribeInfoData;
  open: boolean;
  agentId?: string;
  initialType?: RecordType;
  onOpenChange: (open: boolean) => void;
  onDeleteMessage?: (id: number) => void;
  onReGenerateMessage?: (taskId: string, message: MessageItemData) => void;
  onReedit?: (taskId: string) => void;
}

const dateLabel = (t: TFunction<"translation", undefined>, dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const today = startOfDay(now).getTime();
  const target = startOfDay(d).getTime();
  const oneDay = 24 * 60 * 60 * 1000;

  if (target === today) return t("agent_creation.date.today");
  if (target === today - oneDay) return t("agent_creation.date.yesterday");

  const sameYear = d.getFullYear() === now.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if (sameYear) return `${m}${t("agent_creation.date.month")}${day}${t("agent_creation.date.day")}`;
  return `${d.getFullYear()}${t("agent_creation.date.year")}${m}${t("agent_creation.date.month")}${day}${t("agent_creation.date.day")}`;
};

export const PanelAgentRecords: React.FC<AgentPanelRecordsProps> = ({
  open,
  isExpired,
  expiredAt,
  subscribeInfo,
  onOpenChange,
  agentId,
  initialType = 1,
  onDeleteMessage,
  onReGenerateMessage,
  onReedit,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<RecordType>(initialType);
  const { agent } = agentsHooks.useCurrentAgent();

  const { groups: videoGroups, loadMore: loadMoreVideo, loadingMore: loadingMoreVideo, deleteMessage: deleteVideo, deleteLoading: deleteVideoLoading } = agentUserMessagesHooks.useAgentUserMessages({ agentId, type: "video" });

  const { groups: podcastGroups, loadMore: loadMorePodcast, loadingMore: loadingMorePodcast, deleteMessage: deletePodcast, deleteLoading: deletePodcastLoading } = agentUserMessagesHooks.useAgentUserMessages({ agentId, type: "podcast" });

  const { groups: momentsGroups, loadMore: loadMoreMoments, loadingMore: loadingMoreMoments, deleteMessage: deleteMoment, deleteLoading: deleteMomentLoading } = agentUserMessagesHooks.useAgentUserMessages({ agentId, type: "moments" });

  const { groups: postGroups, loadMore: loadMorePost, loadingMore: loadingMorePost, deleteMessage: deletePost, deleteLoading: deletePostLoading } = agentUserMessagesHooks.useAgentUserMessages({ agentId, type: "post" });

  const { groups: articleGroups, loadMore: loadMoreArticle, loadingMore: loadingMoreArticle, deleteMessage: deleteArticle, deleteLoading: deleteArticleLoading } = agentUserMessagesHooks.useAgentUserMessages({ agentId, type: "article" });

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{ videoUrl: string; poster?: string; text: string; autoPush: boolean; taskId: string; id: number } | null>(null);
  const [momentModalOpen, setMomentModalOpen] = useState(false);
  const [selectedMoment, setSelectedMoment] = useState<{ images: string[]; text: string; autoPush: boolean; taskId: string; id: number } | null>(null);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<{ images: string[]; title: string; text: string; autoPush: boolean; taskId: string; id: number } | null>(null);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<{ title: string; summary: string; contentHtml: string; image?: string; publishedAt?: number; autoPush: boolean; taskId: string; id: number } | null>(null);
  const [aiCreationVideoOpen, setAiCreationVideoOpen] = useState(false);
  const [selectedAiCreationVideo, setSelectedAiCreationVideo] = useState<{
    videoUrl: string;
    poster?: string;
    text: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
    aspectRatio?: string;
    model?: string;
    duration?: number;
  } | null>(null);
  const [aiCreationImageOpen, setAiCreationImageOpen] = useState(false);
  const [selectAiCreationImage, setSelectAiCreationImage] = useState<{
    imageUrls: string[];
    text: string;
    id: number;
    taskId: string;
    autoPush?: boolean;
    aspectRatio?: string;
    model?: string;
    imageSize?: string;
    inputAddress?: string;
  } | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number | null>(null);
  const [activePodcastId, setActivePodcastId] = useState<string | null>(null); // 当前活动的播客ID
  const videoRef = useRef<HTMLDivElement>(null);
  const podcastRef = useRef<HTMLDivElement>(null);
  const momentsRef = useRef<HTMLDivElement>(null);
  const postRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLDivElement>(null);
  const aiContainerRef = useRef<HTMLDivElement>(null);
  const initialRef = useRef(false);
  // 选中的AI创作记录ID
  const [selectedAiRecordIds, setSelectedAiRecordIds] = useState<number[]>([]);

  const tabs: Array<{ key: RecordType; label: string, visible: boolean }> = useMemo(() => {
    // 订阅设置开启或者有历史记录才显示
    return [
      { key: 1, label: t('agent_creation.video'), visible: Boolean(subscribeInfo?.videoSubscribe || videoGroups?.length) },
      { key: 2, label: t('agent_creation.podcast'), visible: Boolean(subscribeInfo?.blogSubscribe || podcastGroups?.length) },
      { key: 3, label: t('agent_creation.moments'), visible: Boolean(subscribeInfo?.momentsSubscribe || momentsGroups?.length) },
      { key: 4, label: t('agent_creation.post'), visible: Boolean(subscribeInfo?.graphicSubscribe || postGroups?.length) },
      { key: 5, label: t('agent_creation.article'), visible: Boolean(subscribeInfo?.articleSubscribe || articleGroups?.length) },
      { key: 6, label: t('agent_creation.ai_creation'), visible: Boolean(subscribeInfo?.aiSubscribe) },
    ]
  }, [subscribeInfo, videoGroups, podcastGroups, momentsGroups, postGroups, articleGroups]);

  // AI创作过滤项
  const aiFilterTabs = [
    { key: 0, label: t('ai_creation.record.all') },
    { key: 1, label: t('ai_creation.record.image') },
    { key: 2, label: t('ai_creation.record.video') },
  ]

  useEffect(() => {
    // 定义检测顺序和对应的类型 Key
    const checkList = [
      { visible: Boolean(subscribeInfo?.videoSubscribe || videoGroups?.length), key: 1 as RecordType },
      { visible: Boolean(subscribeInfo?.blogSubscribe || podcastGroups?.length), key: 2 as RecordType },
      { visible: Boolean(subscribeInfo?.momentsSubscribe || momentsGroups?.length), key: 3 as RecordType },
      { visible: Boolean(subscribeInfo?.graphicSubscribe || postGroups?.length), key: 4 as RecordType },
      { visible: Boolean(subscribeInfo?.articleSubscribe || articleGroups?.length), key: 5 as RecordType },
    ];

    // 找到第一个满足 visible 为 true 的项
    const firstVisible = checkList.find(item => item.visible);

    if (firstVisible && !initialRef.current) {
      setType(firstVisible.key);
      initialRef.current = true; // 暂时处理
    }
  }, [
    subscribeInfo?.videoSubscribe,
    subscribeInfo?.blogSubscribe,
    subscribeInfo?.momentsSubscribe,
    subscribeInfo?.graphicSubscribe,
    subscribeInfo?.articleSubscribe,
    videoGroups?.length,
    podcastGroups?.length,
    momentsGroups?.length,
    postGroups?.length,
    articleGroups?.length
  ]);

  // 删除
  const handleDelete = async (id: number) => {
    switch (type) {
      case 1:
        await deleteVideo(id);
        break;
      case 2:
        await deletePodcast(id);
        break;
      case 3:
        await deleteMoment(id);
        break;
      case 4:
        await deletePost(id);
        break;
      case 5:
        await deleteArticle(id);
        break;
      default:
        break;
    }
    // 同步外面消息列表
    onDeleteMessage?.(id);
  }

  // 重新生成
  const handleRegenerate = async (taskId: string, message?: MessageItemData) => {
    onReGenerateMessage?.(taskId, message);
  }

  const daysVideo = useMemo(() => {
    const g = videoGroups || [];
    const map = new Map<string, any[]>();
    for (const x of g) { const arr = map.get(x.date) ?? []; x.messages.forEach((m) => arr.push(m)); map.set(x.date, arr); }
    return Array.from(map.entries()).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
  }, [videoGroups]);
  const daysPodcast = useMemo(() => {
    const g = podcastGroups || [];
    const map = new Map<string, any[]>();
    for (const x of g) { const arr = map.get(x.date) ?? []; x.messages.forEach((m) => arr.push(m)); map.set(x.date, arr); }
    return Array.from(map.entries()).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
  }, [podcastGroups]);
  const daysMoments = useMemo(() => {
    const g = momentsGroups || [];
    const map = new Map<string, any[]>();
    for (const x of g) { const arr = map.get(x.date) ?? []; x.messages.forEach((m) => arr.push(m)); map.set(x.date, arr); }
    return Array.from(map.entries()).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
  }, [momentsGroups]);
  const daysPost = useMemo(() => {
    const g = postGroups || [];
    const map = new Map<string, any[]>();
    for (const x of g) { const arr = map.get(x.date) ?? []; x.messages.forEach((m) => arr.push(m)); map.set(x.date, arr); }
    return Array.from(map.entries()).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
  }, [postGroups]);
  const daysArticle = useMemo(() => {
    const g = articleGroups || [];
    const map = new Map<string, any[]>();
    for (const x of g) { const arr = map.get(x.date) ?? []; x.messages.forEach((m) => arr.push(m)); map.set(x.date, arr); }
    return Array.from(map.entries()).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime());
  }, [articleGroups]);

  useEffect(() => { const el = videoRef.current; if (!el) return; const onScroll = async () => { const rem = el.scrollHeight - el.clientHeight - el.scrollTop; if (rem <= 200 && !loadingMoreVideo) { const prevH = el.scrollHeight; const prevT = el.scrollTop; try { await loadMoreVideo?.(); } finally { const diff = el.scrollHeight - prevH; el.scrollTop = prevT + diff; } } }; el.addEventListener("scroll", onScroll); return () => el.removeEventListener("scroll", onScroll); }, [loadingMoreVideo, loadMoreVideo]);
  useEffect(() => { const el = podcastRef.current; if (!el) return; const onScroll = async () => { const rem = el.scrollHeight - el.clientHeight - el.scrollTop; if (rem <= 200 && !loadingMorePodcast) { const prevH = el.scrollHeight; const prevT = el.scrollTop; try { await loadMorePodcast?.(); } finally { const diff = el.scrollHeight - prevH; el.scrollTop = prevT + diff; } } }; el.addEventListener("scroll", onScroll); return () => el.removeEventListener("scroll", onScroll); }, [loadingMorePodcast, loadMorePodcast]);
  useEffect(() => { const el = momentsRef.current; if (!el) return; const onScroll = async () => { const rem = el.scrollHeight - el.clientHeight - el.scrollTop; if (rem <= 200 && !loadingMoreMoments) { const prevH = el.scrollHeight; const prevT = el.scrollTop; try { await loadMoreMoments?.(); } finally { const diff = el.scrollHeight - prevH; el.scrollTop = prevT + diff; } } }; el.addEventListener("scroll", onScroll); return () => el.removeEventListener("scroll", onScroll); }, [loadingMoreMoments, loadMoreMoments]);
  useEffect(() => { const el = postRef.current; if (!el) return; const onScroll = async () => { const rem = el.scrollHeight - el.clientHeight - el.scrollTop; if (rem <= 200 && !loadingMorePost) { const prevH = el.scrollHeight; const prevT = el.scrollTop; try { await loadMorePost?.(); } finally { const diff = el.scrollHeight - prevH; el.scrollTop = prevT + diff; } } }; el.addEventListener("scroll", onScroll); return () => el.removeEventListener("scroll", onScroll); }, [loadingMorePost, loadMorePost]);
  useEffect(() => { const el = articleRef.current; if (!el) return; const onScroll = async () => { const rem = el.scrollHeight - el.clientHeight - el.scrollTop; if (rem <= 200 && !loadingMoreArticle) { const prevH = el.scrollHeight; const prevT = el.scrollTop; try { await loadMoreArticle?.(); } finally { const diff = el.scrollHeight - prevH; el.scrollTop = prevT + diff; } } }; el.addEventListener("scroll", onScroll); return () => el.removeEventListener("scroll", onScroll); }, [loadingMoreArticle, loadMoreArticle]);

  const { aiMakeMessageRecords, aiCreationTotal, imageTotal, videoTotal, loading, filterType, setFilterType, deleteAiCreationRecord, deleteCreationOneofRecordLoading, hasMore, loadingMore: loadingMoreAiCreation, loadMore: loadMoreAiCreation } = agentUserMessagesHooks.useAiCreationRecord();

  // Ai创作记录滚动事件,触底加载更多记录
  useEffect(() => {
    const el = aiContainerRef.current;
    if (!el) return;
    const onScroll = async () => {
      const rem = el.scrollHeight - el.clientHeight - el.scrollTop;
      if (rem <= 200 && !loadingMoreAiCreation && hasMore) {
        const prevH = el.scrollHeight;
        const prevT = el.scrollTop;
        try {
          await loadMoreAiCreation?.();
        } finally {
          const diff = el.scrollHeight - prevH;
          el.scrollTop = prevT + diff;
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadingMoreAiCreation, loadMoreAiCreation, hasMore]);

  // Ai创作记录相关
  const handleSelectedAiRecord = (id: number, checked: boolean) => {
    setSelectedAiRecordIds(prev => {
      const nextSet = new Set(prev);
      if (checked) {
        nextSet.add(id);
      } else {
        nextSet.delete(id);
      }
      return Array.from(nextSet);
    });
  }

  // 处理取消选择/退出多选模式
  const handleCancelSelectAiRecord = () => {
    setSelectedAiRecordIds([]);
  }

  // 处理全选/反选所有文件夹
  const handleSelectedAllAiRecord = (checked: boolean) => {
    setSelectedAiRecordIds(prev => {
      if (checked) {
        return aiMakeMessageRecords.map(item => item.id);
      }
      return [];
    });
  }

  const [materialOpen, setMaterialOpen] = useState(false);
  // 素材标签hook
  const { createTag, loading: createLoading } = assetsTagsHook.useCreateAssetsTag();
  const { batchUpload, loading: uploadLoading } = assetsTagsHook.useBatchUploadFolderTags();
  // 素材文件夹hook
  const { createMaterial, loading: createFolderLoading } = assetsMaterialHooks.useMaterialFolderCreate();

  const handleSaveToMaterial = async (data: { newFolderId: number; newTagIds: number[]; }[]) => {
    try {
      const urls = aiMakeMessageRecords.filter(item => selectedAiRecordIds.includes(item.id)).map(item => item.s3Url);
      await batchUpload(urls.map(url => ({ url, target: data })));
      setSelectedAiRecordIds([]);
      // onSuccess?.();
    } catch (error) {
      console.error("Failed to batch upload assets material item tags:", error);
      throw error;
    }
  }

  // 批量删除
  const handleBatchDeleteAiRecord = async () => {
    try {
      await deleteAiCreationRecord(aiMakeMessageRecords.filter(item => selectedAiRecordIds.includes(item.id)).map(item => ({ id: item.id, type: item.type })));
      handleCancelSelectAiRecord();
    } catch (error) {
      console.error('error:', error);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className="
            fixed right-0 top-0 z-50 h-full w-[600px] bg-white
            flex flex-col
            data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full
            transition-transform duration-300 pb-4
          "
        >
          {/* 顶部标题与关闭 */}
          <div className="flex items-center justify-between mt-6 px-6 h-10 mb-2">
            <div className="text-base font-medium text-[#111827]">
              {t('agent_creation.agent_modal.generate_record')}
            </div>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              onClick={() => onOpenChange(false)}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </div>

          <div className="mb-3 px-4 gap-3 flex">
            {tabs.filter((t) => t.visible).map(item => (
              <div
                key={item.key}
                className={cn(
                  "py-2 px-4 rounded-[8px] text-sm text-[rgba(102,112,133,1)] font-medium cursor-pointer transition-all hover:text-[rgba(16,24,40,1)]",
                  type === item.key && "bg-[rgba(16,24,40,0.05)] text-[rgba(16,24,40,1)]",
                )}
                onClick={() => setType(item.key)}
              >
                {item.label}
              </div>
            ))}
          </div>
          {isExpired && (
            <div className="px-6 mb-4">
              <div className="text-[rgba(16,24,40,1)] font-medium text-base px-[10px] py-1.5 bg-[rgba(247,247,247,1)] rounded-md flex items-center gap-2">
                <ClockRewindIcon className=" text-[rgba(16,24,40,1)] flex-shrink-0" />
                {t('agent_creation.server_expired', { expiredAt: dayjs(expiredAt).add(6, 'month').format('YYYY-MM-DD') })}
              </div>
            </div>
          )}
          <div ref={videoRef} className="flex-1 overflow-y-auto px-6 pb-6" style={{ display: type === 1 ? 'block' : 'none' }}>
            <div className="h-full flex flex-col">
              {daysVideo.length > 0 && <div className="flex flex-col gap-6">
                {daysVideo.map(([date, items]) => (
                  <div key={date} className="flex flex-col gap-4">
                    <div className="text-sm text-[rgba(52,64,84,1)]">{dateLabel(t, date)}</div>
                    <div className="flex flex-col gap-4">
                      {items.map((m) => {
                        const status = typeof m.status === "number" ? m.status : parseInt(String(m.status || "0"), 10) || 0;
                        if (status === 0) return (<ShortVideoGeneratingItem key={m.id} createdAt={m.createdAt || Date.now()} content={m.content || ""} />);
                        if (status >= 2) return (
                          <ShortVideoFailedItem
                            key={m.id}
                            content={m.content || ""}
                            onDelete={() => handleDelete(m.id)}
                            deleteLoading={deleteVideoLoading}
                            onRegenerate={() => handleRegenerate(m.taskId || "", m)}
                          />
                        );
                        return (
                          <ShortVideoRecordItem
                            key={m.id}
                            thumbnail={m.s3CoverImageUrl || m.coverImageUrl || ""}
                            content={m.content || ""}
                            duration={typeof m.videoDuration === "number" ? m.videoDuration : undefined}
                            deleteLoading={deleteVideoLoading}
                            onDelete={() => handleDelete(m.id)}
                            onTap={() => {
                              setSelectedVideo({
                                videoUrl: String(m.videoUrl || ""),
                                poster: String(m.coverImageUrl || ""),
                                text: m.content || "",
                                autoPush: m.autoPush,
                                taskId: m.taskId || "",
                                id: m.id,
                              });
                              setVideoModalOpen(true);
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>}
              {daysVideo.length <= 0 && (
                <div className="h-full flex-1 flex flex-col items-center justify-center gap-3">
                  <RecordEmptyImage className="w-[147px]  text-[rgba(52,64,84,1)]" />
                  <span className="text-base text-[rgba(102,112,133,1)]">{t('agent_creation.empty_tips')}</span>
                </div>
              )}
            </div>
          </div>

          <div ref={podcastRef} className="flex-1 overflow-y-auto px-6 pb-6" style={{ display: type === 2 ? 'block' : 'none' }}>
            <div className="h-full flex flex-col">
              {daysPodcast.length > 0 && <div className="flex flex-col gap-6">
                {daysPodcast.map(([date, items]) => (
                  <div key={date} className="flex flex-col gap-4">
                    <div className="text-[15px] text-[rgba(52,64,84,1)] font-medium">{dateLabel(t, date)}</div>
                    <div className="flex flex-col gap-4">
                      {items.map((m) => {
                        const status = typeof m.status === "number" ? m.status : parseInt(String(m.status || "0"), 10) || 0;
                        if (status === 0) return (<PodcastGeneratingItem key={m.id} createdAt={m.createdAt || Date.now()} content={m.content || m.title || ""} />);
                        if (status >= 2) return (
                          <PodcastFailedItem
                            key={m.id}
                            content={m.content || m.title || ""}
                            onDelete={() => handleDelete(m.id)}
                            deleteLoading={deletePodcastLoading}
                            onRegenerate={() => handleRegenerate(m.taskId || "", m)}
                          />
                        );
                        return (
                          <PodcastRecordItem
                            key={m.id}
                            coverUrl={m.s3CoverImageUrl || m.coverImageUrl || ""}
                            content={m.content || m.title || ""}
                            audioUrl={m.s3AudioUrl || m.audioUrl || ""}
                            duration={typeof m.audioDuration === "number" ? m.audioDuration : undefined}
                            isActive={activePodcastId === m.id}
                            onDelete={() => handleDelete(m.id)}
                            deleteLoading={deletePodcastLoading}
                            onTogglePlay={(showControls) => {
                              setActivePodcastId(showControls ? m.id : null);
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>}
              {daysPodcast.length <= 0 && (
                <div className="h-full flex-1 flex flex-col items-center justify-center gap-3">
                  <RecordEmptyImage className="w-[147px]  text-[rgba(52,64,84,1)]" />
                  <span className="text-base text-[rgba(102,112,133,1)]">{t('agent_creation.empty_tips')}</span>
                </div>
              )}
            </div>
          </div>

          <div ref={momentsRef} className="flex-1 overflow-y-auto px-6 pb-6" style={{ display: type === 3 ? 'block' : 'none' }}>
            <div className="h-full flex flex-col">
              {daysMoments.length > 0 && <div className="flex flex-col gap-6">
                {daysMoments.map(([date, items]) => (
                  <div key={date} className="flex flex-col gap-4">
                    <div className="text-[15px] text-[rgba(52,64,84,1)] font-medium">{dateLabel(t, date)}</div>
                    <div className="flex flex-col gap-4">
                      {items.map((m) => {
                        const status = typeof m.status === "number" ? m.status : parseInt(String(m.status || "0"), 10) || 0;
                        const images = String(m.imageUrls || "").split(",").filter(Boolean);
                        if (status === 0) return (<MomentsGeneratingItem key={m.id} createdAt={(m).createdAt || Date.now()} content={m.content || ""} />);
                        if (status >= 2) return (
                          <MomentsFailedItem
                            key={m.id}
                            content={m.content || ""}
                            deleteLoading={deleteMomentLoading}
                            onDelete={() => handleDelete(m.id)}
                            onRegenerate={() => handleRegenerate(m.taskId, m)}
                          />
                        );
                        return (
                          <MomentsRecordItem
                            key={m.id}
                            images={images}
                            content={m.content || ""}
                            onDelete={() => handleDelete(m.id)}
                            deleteLoading={deleteMomentLoading}
                            onTap={() => {
                              setSelectedMoment({
                                images, text: m.content || "",
                                autoPush: m.autoPush,
                                taskId: m.taskId,
                                id: m.id,
                              });
                              setMomentModalOpen(true);
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>}
              {daysMoments.length <= 0 && (
                <div className="h-full flex-1 flex flex-col items-center justify-center gap-3">
                  <RecordEmptyImage className="w-[147px]  text-[rgba(52,64,84,1)]" />
                  <span className="text-base text-[rgba(102,112,133,1)]">{t('agent_creation.empty_tips')}</span>
                </div>
              )}
            </div>
          </div>

          <div ref={postRef} className="flex-1 overflow-y-auto px-6 pb-6" style={{ display: type === 4 ? 'block' : 'none' }}>
            <div className="h-full flex flex-col">
              {daysPost.length > 0 && <div className="flex flex-col gap-6">
                {daysPost.map(([date, items]) => (
                  <div key={date} className="flex flex-col gap-4">
                    <div className="text-[15px] text-[rgba(52,64,84,1)] font-medium">{dateLabel(t, date)}</div>
                    <div className="flex flex-col gap-4">
                      {items.map((m) => {
                        const status = typeof m.status === "number" ? m.status : parseInt(String(m.status || "0"), 10) || 0;
                        const images = String(m.imageUrls || "").split(",").filter(Boolean);
                        if (status === 0) return (<PostGeneratingItem key={m.id} createdAt={m.createdAt || Date.now()} content={m.content || ""} />);
                        if (status >= 2) return (
                          <PostFailedItem
                            key={m.id}
                            content={m.content || ""}
                            onDelete={() => handleDelete(m.id)}
                            deleteLoading={deletePostLoading}
                            onRegenerate={() => handleRegenerate(m.taskId || "", m)}
                          />
                        );
                        return (
                          <PostRecordItem
                            key={m.id}
                            images={images}
                            title={m.title || ""}
                            deleteLoading={deletePostLoading}
                            onTap={() => {
                              setSelectedPost({
                                images,
                                text: m.content || "",
                                autoPush: m.autoPush,
                                taskId: m.taskId,
                                id: m.id,
                                title: m.title || "",
                              });
                              setPostModalOpen(true);
                            }}
                            onDelete={() => handleDelete(m.id)}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>}
              {daysPost.length <= 0 && (
                <div className="h-full flex-1 flex flex-col items-center justify-center gap-3">
                  <RecordEmptyImage className="w-[147px]  text-[rgba(52,64,84,1)]" />
                  <span className="text-base text-[rgba(102,112,133,1)]">{t('agent_creation.empty_tips')}</span>
                </div>
              )}
            </div>
          </div>

          <div ref={articleRef} className="flex-1 overflow-y-auto px-6 pb-6" style={{ display: type === 5 ? 'block' : 'none' }}>
            <div className="h-full flex flex-col">
              {daysArticle.length > 0 && <div className="flex flex-col gap-6">
                {daysArticle.map(([date, items]) => (
                  <div key={date} className="flex flex-col gap-4">
                    <div className="text-[15px] text-[rgba(52,64,84,1)] font-medium">{dateLabel(t, date)}</div>
                    <div className="flex flex-col gap-4">
                      {items.map((m) => {
                        const status = typeof m.status === "number" ? m.status : parseInt(String(m.status || "0"), 10) || 0;
                        if (status === 0) return (<ArticleGeneratingItem key={m.id} createdAt={m.createdAt || Date.now()} title={m.title || ""} />);
                        if (status >= 2) return (
                          <ArticleFailedItem
                            key={m.id}
                            title={m.title || ""}
                            onDelete={() => handleDelete(m.id)}
                            deleteLoading={deleteArticleLoading}
                            onRegenerate={() => handleRegenerate(m.taskId, m)}
                          />
                        );
                        return (
                          <ArticleRecordItem
                            key={m.id}
                            coverUrl={m.s3CoverImageUrl || m.coverImageUrl || ""}
                            title={m.title || ""}
                            deleteLoading={deleteArticleLoading}
                            onDelete={() => handleDelete(m.id)}
                            onTap={() => {
                              setSelectedArticle({
                                title: m.title || "",
                                contentHtml: String(m.content || ""),
                                image: String(m.s3CoverImageUrl || m.coverImageUrl || ""),
                                publishedAt: m.createdAt,
                                summary: String(m.summary || ""),
                                autoPush: m.autoPush,
                                taskId: m.taskId,
                                id: m.id,
                              });
                              setArticleModalOpen(true);
                            }}
                          />);
                      })}
                    </div>
                  </div>
                ))}
              </div>}
              {daysArticle.length <= 0 && (
                <div className="h-full flex-1 flex flex-col items-center justify-center gap-3">
                  <RecordEmptyImage className="w-[147px]  text-[rgba(52,64,84,1)]" />
                  <span className="text-base text-[rgba(102,112,133,1)]">{t('agent_creation.empty_tips')}</span>
                </div>
              )}
            </div>
          </div>


          {/* AI创作过滤项 */}
          {type === 6 && (
            <div className="w-full flex gap-2 mb-3 px-4">
              {aiFilterTabs.map(item => (
                <div
                  key={item.key}
                  className={cn(
                    "py-2 px-4 text-sm font-medium text-[rgba(102,112,133,1)] cursor-pointer hover:text-[rgba(16,24,40,1)]",
                    filterType === item.key && "text-[rgba(16,24,40,1)]",
                  )}
                  onClick={() => setFilterType(item.key)}
                >{item.label}({item.key === 0 ? aiCreationTotal : item.key === 1 ? imageTotal : videoTotal})</div>
              ))}
            </div>
          )}
          <div ref={aiContainerRef} className="relative flex-1 overflow-y-auto px-6 pb-6" style={{ display: type === 6 ? 'block' : 'none' }}>
            {aiMakeMessageRecords.length > 0 && (
              <>
                <AiRecordList
                  records={aiMakeMessageRecords}
                  selectedIds={selectedAiRecordIds}
                  onSelected={handleSelectedAiRecord}
                  onPreview={(id: number) => {
                    const i = aiMakeMessageRecords.findIndex(item => item.id === id);
                    if (i !== -1) {
                      setOpenPreview(true);
                      setCurrentPreviewIndex(i);
                    }
                  }}
                />
                {loadingMoreAiCreation && (
                  <div className="py-4 text-center text-sm text-gray-500">
                  </div>
                )}
              </>
            )}
            {aiMakeMessageRecords.length <= 0 && !loading && (
              <div className="h-full flex-1 flex flex-col items-center justify-center gap-3">
                <RecordEmptyImage className="w-[147px]  text-[rgba(52,64,84,1)]" />
                <span className="text-base text-[rgba(102,112,133,1)]">{t('agent_creation.empty_tips')}</span>
              </div>
            )}
          </div>
          <LoadingOverlay loading={loading} />

          {selectedVideo && (
            <AgentVideoModal
              open={videoModalOpen}
              onOpenChange={(open) => { setVideoModalOpen(open); if (!open) setSelectedVideo(null); }}
              videoUrl={selectedVideo.videoUrl}
              poster={selectedVideo.poster}
              avatarUrl={agent?.subAvatar || ''}
              nickname={agent?.subName || ''}
              text={selectedVideo.text}
              isAutoPush={selectedVideo?.autoPush}
              deleteLoading={deleteVideoLoading}
              onDelete={() => handleDelete(selectedVideo?.id)}
              onReedit={() => {
                onReedit(selectedVideo.taskId)
                onOpenChange(false)
              }}
              onRegenerate={() => {
                handleRegenerate(selectedVideo?.taskId)
                onOpenChange(false)
              }}
            />
          )}
          {selectedMoment && (
            <AgentMomentModal
              open={momentModalOpen}
              onOpenChange={(open) => { setMomentModalOpen(open); if (!open) setSelectedMoment(null); }}
              avatarUrl={agent?.subAvatar || ''}
              nickname={agent?.subName || ''}
              text={selectedMoment.text}
              images={selectedMoment.images}
              isAutoPush={selectedMoment?.autoPush}
              deleteLoading={deleteMomentLoading}
              onDelete={() => handleDelete(selectedMoment?.id)}
              onReedit={() => {
                onReedit(selectedMoment.taskId)
                onOpenChange(false)
              }}
              onRegenerate={() => {
                handleRegenerate(selectedMoment?.taskId)
                onOpenChange(false)
              }}
            />
          )}
          {selectedPost && (
            <AgentPostModal
              open={postModalOpen}
              onOpenChange={(open) => { setPostModalOpen(open); if (!open) setSelectedPost(null); }}
              avatarUrl={agent?.subAvatar || ''}
              nickname={agent?.subName || ''}
              title={selectedPost.title}
              text={selectedPost.text}
              images={selectedPost.images}
              isAutoPush={selectedPost?.autoPush}
              deleteLoading={deletePostLoading}
              onDelete={() => handleDelete(selectedPost?.id)}
              onReedit={() => {
                onReedit(selectedPost.taskId)
                onOpenChange(false)
              }}
              onRegenerate={() => {
                handleRegenerate(selectedPost?.taskId)
                onOpenChange(false)
              }}
            />
          )}
          {selectedArticle && (
            <AgentAtricleModal
              quote={selectedArticle.summary}
              open={articleModalOpen}
              onOpenChange={(open) => { setArticleModalOpen(open); if (!open) setSelectedArticle(null); }}
              title={selectedArticle.title}
              contentHtml={selectedArticle.contentHtml}
              avatarUrl={agent?.subAvatar || ''}
              nickname={agent?.subName || ''}
              publishedAt={selectedArticle.publishedAt}
              imageUrl={selectedArticle.image}
              onCopyQuote={(q) => navigator.clipboard.writeText(q)}
              isAutoPush={selectedArticle?.autoPush}
              deleteLoading={deleteArticleLoading}
              onDelete={() => handleDelete(selectedArticle?.id)}
              onReedit={() => {
                onReedit(selectedArticle.taskId)
                onOpenChange(false)
              }}
              onRegenerate={() => {
                handleRegenerate(selectedArticle?.taskId)
                onOpenChange(false)
              }}
            />
          )}
          {/* <LoadingOverlay loading={deleteVideoLoading || deletePodcastLoading || deleteMomentLoading || deletePostLoading || deleteArticleLoading} /> */}
          {/* 工具栏 */}
          <div
            className={cn(
              "transition-all duration-300 ease-in-out",
              (selectedAiRecordIds.length > 0) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <AssetsToolBar
              onClose={handleCancelSelectAiRecord}
              isSelectedAll={aiMakeMessageRecords.length > 0 && selectedAiRecordIds.length === aiMakeMessageRecords?.length}
              selectedAmount={selectedAiRecordIds.length}
              onSelectedAll={handleSelectedAllAiRecord}
              deleteLoading={deleteCreationOneofRecordLoading}
              onDelete={handleBatchDeleteAiRecord}
              deleteTitle={t('agent_dashboard.company_asset.delete')}
              toolNode={
                <Tooltip
                  side='top'
                  align='center'
                  offset={20}
                  trigger={
                    <IconButton
                      className='hover:bg-white/30 size-8 rounded-[8px]'
                      icon={<MaterialIcon className='text-white size-5' />}
                      onClick={() => setMaterialOpen(true)}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("ai_creation.save_to_material")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
              }
            />
            {materialOpen && (
              <ModalTagMove
                open={open}
                title={t("ai_creation.save_to_material")}
                moveButtonText={t("common.cancel")}
                copyButtonText={t("common.save")}
                moveButtonClassName='!px-10'
                copyButtonClassName='!px-10'
                moveButtonDisabled={false}
                createFolderLoading={createFolderLoading}
                createTagLoading={createLoading}
                saveLoading={uploadLoading}
                onOpenChange={setMaterialOpen}
                onCreateFolder={(name) => createMaterial({ name })}
                onCreateTag={(name, folderId) => createTag(name, folderId)}
                onCopy={handleSaveToMaterial}
              />
            )}
            {openPreview && (
              <PreviewVideoWithImageFull
                previewList={aiMakeMessageRecords.map(item => ({
                  id: item.id,
                  url: item.s3Url || item.url,
                  type: item.type === 1 ? 'image' : 'video',
                }))}
                currentIndex={currentPreviewIndex}
                open={openPreview}
                onOpenChange={setOpenPreview}
              />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
